import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';

@Component({
  selector: 'rcp-list-item',
  template: `
    <div class="rcp-list-item" [class.active]="active()" [class.disabled]="disabled()" [class.clickable]="clickable()">
      <div class="rcp-list-item-part">
        @if (icon()) {
          <span class="rcp-list-item-icon">
            <fa-icon [icon]="icon()" />
          </span>
        }
        <ng-content select="[leading-detail]"></ng-content>

        @if (!!label()) {
          <!-- unfortunately can't add an enter/space between >{{, it shows up in the UI-->
          <span class="rcp-list-item-label text max-2-lines">{{ label() }}</span>
        }
      </div>
      <div class="rcp-list-item-part">
        <ng-content />
        @if (count() !== undefined) {
          <span class="rcp-list-item-count">{{ count() }}</span>
        }
        <ng-content select="[trailing-detail]"></ng-content>
      </div>
    </div>
  `,
  styles: `
    :host {
      width: var(--rcp-list-item-width, inherit);
    }
    .rcp-list-item {
      display: flex;
      justify-content: var(--rcp-list-item-justify, space-between);
      align-items: center;
      max-height: var(--rcp-list-item-height, inherit);

      break-inside: avoid; /* Prevent clipping in list-container's columns */

      padding: var(--item-padding-sm) var(--rcp-list-item-padding, var(--item-padding-md));
      gap: var(--item-internal-spacing-md);

      border: 1px solid transparent;
      border-radius: var(--rcp-list-item-border-radius, var(--border-radius-md));

      color: var(--col-text-primary);

      .rcp-list-item-part {
        display: flex;
        align-items: center;
        gap: var(--item-internal-spacing-md);

        &:empty {
          display: none;
        }

        .rcp-list-item-icon {
          /* matches so icon width and other leading-detail content is matched correctly*/
          width: var(--rcp-list-item-icon-width, 24px);
          text-align: var(--rcp-list-item-icon-align, center);
          flex-shrink: 0;
        }
      }

      .rcp-list-item-label:empty {
        display: none;
      }

      .rcp-list-item-count {
        font-size: var(--font-size-small);
        color: var(--col-text-tertiary);
      }

      &.clickable {
        &:hover:not(.disabled):not(.active) {
          background-color: var(--col-active-bg);
          color: var(--col-active-text);
          cursor: var(--rcp-list-item-cursor, pointer);
        }

        /* pseudo-class :active is not the same as the .active class */
        &:active:not(.disabled):not(.active) {
          border-color: var(--col-active-lines);
          background-color: var(--col-active-bg);
          box-shadow:
            0px 0px 0px 2px var(--col-active-bg),
            var(--effect-shadow-sm);
        }

        &:focus:not(.disabled) {
          box-shadow: var(--effect-shadow-sm);
          box-shadow: 0px 0px 0px 2px var(--col-active-bg);
        }
      }

      &.active:not(.disabled) {
        background-color: var(--col-list-item-active);
        color: var(--col-text-primary);
      }

      &.disabled {
        color: var(--col-disabled-contrast);
      }
    }
  `,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ListItemComponent {
  icon = input<IconProp | undefined>(undefined);
  label = input.required<string>();
  count = input<string | number | undefined>();
  active = input<boolean>(false);
  disabled = input<boolean>(false);
  clickable = input<boolean>(true);
}
